import type { LoaderFunction } from '@remix-run/node';
import { getClientIPAddress } from 'remix-utils/get-client-ip-address';
import { promiseHash } from 'remix-utils/promise';
import type { Click, InternalCreditCard, UserDetails } from '@cardo/types';
import { getOfferLink, getTrackingValue } from '~/lib/creditCard';
import { influencerAllowedOnMediaNetwork } from '~/lib/utils';
import { subAffiliateCookie } from '~/lib/server/cookies.server';
import { fetchStrapi } from '~/lib/server/api.server';
import {
  useRouteError,
  isRouteErrorResponse,
  useLoaderData,
  json,
} from '@remix-run/react';
import { DefaultErrorBoundary } from '@cardo/ui';
import NotFoundErrorBoundary from '~/components/error/NotFoundErrorBoundary';
import logoWords from '~/images/logo-words.svg';
import Loader from '~/components/Loader';
import { useInterval } from 'react-use';
import { useEffect, useState } from 'react';
import InfluencerBrand from '~/components/InfluencerBrand';

export function ErrorBoundary() {
  const error = useRouteError();

  if (isRouteErrorResponse(error)) {
    return <NotFoundErrorBoundary />;
  }

  return <DefaultErrorBoundary />;
}

export const loader: LoaderFunction = async ({ params, request }) => {
  try {
    const { cardSlug } = params;
    const url = new URL(request.url);
    const influencerFromParams = url.searchParams.get('influencer');
    const influencerFromCookie = await subAffiliateCookie.parse(
      request.headers.get('Cookie')
    );

    const influencer = influencerFromParams || influencerFromCookie;
    const fromPath = url.searchParams.get('fromPath');

    let influencerMediaNetwork;
    let influencerId;

    const promiseObj = {
      cardsRes: fetchStrapi<InternalCreditCard[]>('/credit-cards/internal', {
        filters: {
          slug: cardSlug,
        },
        populate: {
          creditCardIssuer: {
            fields: ['slug'],
          },
        },
      }),
      influencerRes: influencer
        ? fetchStrapi<UserDetails[]>('/users', {
            filters: { username: influencer },
            populate: 'avatar',
          })
        : Promise.resolve(null),
    };

    const { cardsRes, influencerRes } = await promiseHash(promiseObj);

    console.log('cardsRes: ', JSON.stringify(cardsRes, null, 2));

    if (influencerRes) {
      try {
        if (influencerRes?.length > 0) {
          influencerId = influencerRes[0]?.id;
          influencerMediaNetwork = influencerRes[0]?.mediaNetwork;

          if (influencerRes[0]?.disabled) {
            influencerId = null;
          }
        }
      } catch (err) {
        console.error('could not retrieve influencer details, err: ', err);
      }
    }

    let foundCardByCardName: InternalCreditCard | null = null;
    if (!cardsRes.data || cardsRes.data.length === 0) {
      console.error(
        `Could not find card with slug: ${cardSlug}, trying querying by cardName`
      );

      const creditCardsByCardName = await fetchStrapi<InternalCreditCard[]>(
        '/credit-cards/internal',
        {
          filters: {
            cardName: cardSlug,
          },
          populate: {
            creditCardIssuer: {
              fields: ['slug'],
            },
          },
        }
      );

      if (
        !creditCardsByCardName.data ||
        creditCardsByCardName.data.length === 0
      ) {
        console.error(`Could not find card with cardName: ${cardSlug}`);
        throw new Response('Could not find that offer.', { status: 404 });
      }

      foundCardByCardName = creditCardsByCardName.data[0];
    }

    const foundCard = cardsRes.data?.[0] ?? foundCardByCardName;

    if (!foundCard) {
      console.error(`Could not find card with slug: ${cardSlug}`);
      throw new Response('Could not find that offer.', { status: 404 });
    }

    const cardMediaNetwork = foundCard.attributes.mediaNetwork;
    const isInfluencerAllowedOnMediaNetwork = influencerAllowedOnMediaNetwork(
      cardMediaNetwork,
      influencerMediaNetwork
    );

    const ipAddress = getClientIPAddress(request);

    let offerLink = getOfferLink({
      card: foundCard,
    });

    if (!offerLink) {
      return new Response('Could not find that offer.', { status: 404 });
    }

    try {
      const clickRes = await fetchStrapi<Click>(
        '/clicks',
        {},
        {
          method: 'post',
          body: JSON.stringify({
            data: {
              incomingUrl: url,
              redirectUrl: offerLink,
              influencer:
                isInfluencerAllowedOnMediaNetwork && influencerId
                  ? influencerId
                  : undefined,
              ipAddress: ipAddress || undefined,
              creditCard: foundCard.id,
              userAgent: request.headers.get('User-Agent'),
              mediaNetworkReferredTo: foundCard.attributes.mediaNetwork,
              fromPath: fromPath || undefined,
            },
          }),
        }
      );

      if (clickRes.data && clickRes.data.id) {
        offerLink = getOfferLink({
          card: foundCard,
          trackingValue: getTrackingValue({
            clickId: String(clickRes.data.id),
            influencerId: isInfluencerAllowedOnMediaNetwork
              ? influencerId
              : undefined,
          }),
        });
      }

      if (!offerLink) {
        new Response('Could not find that offer.', { status: 404 });
      }

      return json({
        offerLink,
        cardName: foundCard.attributes.cardName,
        cardArtUrl: foundCard.attributes.cardArtUrl,
        cardIssuerSlug:
          foundCard.attributes.creditCardIssuer?.data?.attributes.slug,
        influencerName: influencerId ? influencerRes[0].username : null,
        influencerAvatar: influencerId ? influencerRes[0].avatar : null,
      });
    } catch (err) {
      console.error(
        `Could not record click for card ${foundCard.id} | ${foundCard.attributes.cardName} and influencerId ${influencerId}. Error: ${err}`
      );

      if (!offerLink) {
        new Response('Could not find that offer.', { status: 404 });
      }

      return json({
        offerLink,
        cardName: foundCard.attributes.cardName,
        cardArtUrl: foundCard.attributes.cardArtUrl,
        cardIssuerSlug:
          foundCard.attributes.creditCardIssuer?.data?.attributes.slug,
        influencerName: influencerId ? influencerRes[0].username : null,
        influencerAvatar: influencerId ? influencerRes[0].avatar : null,
      });
    }
  } catch (err) {
    console.error('Error in go_.$cardSlug loader: ', err);
    return new Response('Could not find that offer.', { status: 404 });
  }
};

export default function Go() {
  const loaderData = useLoaderData<typeof loader>();

  const {
    offerLink,
    cardName,
    cardArtUrl,
    cardIssuerSlug,
    influencerName,
    influencerAvatar,
  } = loaderData;

  const [countdown, setCountdown] = useState(3);
  const [secondsElapsed, setSecondsElapsed] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [hasSetLocationHref, setHasSetLocationHref] = useState(false);

  useInterval(() => {
    if (countdown > 0) {
      setCountdown((prev) => prev - 1);
    }
    setSecondsElapsed((prev) => prev + 1);
  }, 1000);

  useEffect(
    function redirectAfterCountdown() {
      if (countdown <= 1 && offerLink && !hasSetLocationHref) {
        window.location.href = offerLink;
        setHasSetLocationHref(true);
      }
      if (secondsElapsed > 10) {
        setError(
          'Something went wrong getting your offer. Please try clicking that link again.'
        );
      }
    },
    [countdown, hasSetLocationHref, offerLink, secondsElapsed]
  );

  return (
    <div className="flex min-h-screen w-screen justify-center items-center px-2 md:px-0">
      <div className="border rounded-md px-6 py-8 md:px-20 md:py-14 flex flex-col items-center gap-8">
        <div className="flex flex-col items-center gap-5 md:flex-row">
          <div className="flex items-center gap-2 md:gap-4">
            <Loader className="flex-shrink-0" />
            <img
              src={logoWords}
              className="w-[137.25px] h-[16.5px] md:w-[183px] md:h-[22px]"
            />
          </div>
          {influencerName && cardIssuerSlug !== 'chase' && (
            <>
              <div className="border-theme-blue-darkest h-px w-8 md:w-px md:h-14 rounded border" />
              <InfluencerBrand
                displayName={influencerName}
                image={{
                  data: {
                    id: influencerAvatar.id,
                    attributes: influencerAvatar,
                  },
                }}
              />
            </>
          )}
        </div>
        <div className="flex flex-col gap-2 items-center">
          <p className="text-center">
            You are being redirected to our partner offer page for the:
          </p>
          <div className="flex flex-col items-center gap-4 w-11/12">
            <h1 className="text-lg md:text-2xl font-bold text-center md:min-w-[300px] w-3/4">
              {cardName}
            </h1>
            <figure className="flex w-3/4 max-w-[200px] md:max-w-[250px] md:w-full">
              <img
                src={cardArtUrl}
                alt={`card art for the ${cardName}`}
                className="z-10 mx-auto w-full rounded-lg object-cover"
              />
            </figure>
          </div>
        </div>
        {!error && (
          <a href={offerLink} className="text-sm">
            Click here if you aren't redirected in{' '}
            {countdown > 0 ? countdown : '1...'}
          </a>
        )}
        {error && <p className="text-rose-600">{error}</p>}
      </div>
    </div>
  );
}
